import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ClipboardList, Star, CalendarClock } from "lucide-react";

function RatingRow({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex gap-1.5" data-testid={testid}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            type="button"
            key={n}
            data-testid={`${testid}-${n}`}
            onClick={() => onChange(n)}
            className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
              n <= value ? "border-[#0d9488] bg-teal-50 text-[#0d9488]" : "border-slate-200 text-slate-300 hover:border-slate-300"
            }`}
          >
            <Star className="h-4 w-4" fill={n <= value ? "#0d9488" : "none"} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function AssessmentModal({ visit, open, onOpenChange, onSaved, onReschedule }) {
  const [engagement, setEngagement] = useState(3);
  const [emotionalSafety, setEmotionalSafety] = useState(3);
  const [punctuality, setPunctuality] = useState("on_time");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visit) {
      const a = visit.assessment || {};
      setEngagement(a.engagement || 3);
      setEmotionalSafety(a.emotional_safety || 3);
      setPunctuality(a.punctuality || (visit.timeliness === "no_show" ? "no_show" : visit.timeliness === "late" ? "late" : "on_time"));
      setNote(a.observation_note || "");
    }
  }, [visit]);

  if (!visit) return null;

  const save = async () => {
    setLoading(true);
    try {
      await api.put(`/visits/${visit.id}/assessment`, {
        engagement,
        emotional_safety: emotionalSafety,
        punctuality,
        observation_note: note,
      });
      toast.success("Visit quality documented");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="assessment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <ClipboardList className="h-5 w-5 text-[#0d9488]" /> Document Visit Quality
          </DialogTitle>
          <DialogDescription>
            {visit.parent_name} → {visit.child_name} · {visit.visit_date}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <RatingRow label="Engagement" value={engagement} onChange={setEngagement} testid="rating-engagement" />
          <RatingRow label="Emotional safety" value={emotionalSafety} onChange={setEmotionalSafety} testid="rating-emotional-safety" />
          <div className="space-y-1.5">
            <Label>Punctuality</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "on_time", l: "On Time", on: "border-emerald-500 bg-emerald-50 text-emerald-700" },
                { v: "late", l: "Not On Time", on: "border-orange-500 bg-orange-50 text-orange-700" },
                { v: "no_show", l: "No Show", on: "border-red-500 bg-red-50 text-red-700" },
              ].map((o) => (
                <button
                  type="button"
                  key={o.v}
                  data-testid={`punctuality-${o.v}`}
                  onClick={() => setPunctuality(o.v)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    punctuality === o.v ? o.on : "border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Observation notes</Label>
            <Textarea data-testid="assessment-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Describe interactions, behavior, and quality of the visit..." />
          </div>
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            type="button"
            variant="outline"
            data-testid="reschedule-from-assessment-btn"
            onClick={() => { onOpenChange(false); onReschedule?.(visit); }}
            className="border-[#0d9488] text-[#0d9488] hover:bg-teal-50"
          >
            <CalendarClock className="mr-1.5 h-4 w-4" /> Reschedule Instead
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="save-assessment-btn" onClick={save} disabled={loading} className="bg-[#1e3a8a] hover:bg-[#172554]">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Documentation
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
